/** The backend's error shape (RFC 9457 problem details). */
export interface Problem {
  status: number;
  title: string;
  detail: string;
  code: string;
  errors?: string[];
  requestId?: string;
}

/** Every failed API call throws this. Screens switch on `code`, never on the text. */
export class ApiError extends Error {
  readonly problem: Problem;

  constructor(problem: Problem) {
    super(problem.detail);
    this.name = 'ApiError';
    this.problem = problem;
  }

  get code(): string {
    return this.problem.code;
  }
}

export function isProblem(value: unknown): value is Problem {
  return (
    typeof value === 'object' &&
    value !== null &&
    'status' in value &&
    typeof value.status === 'number' &&
    'detail' in value &&
    typeof value.detail === 'string' &&
    'code' in value &&
    typeof value.code === 'string'
  );
}

const NETWORK_ERROR: Problem = {
  status: 0,
  title: 'Network error',
  detail: 'Could not reach the server. Check your connection and try again.',
  code: 'network_error',
};

export function toApiError(error: unknown, response?: Response): ApiError {
  if (error instanceof ApiError) return error;
  if (isProblem(error)) return new ApiError(error);
  if (response && !response.ok) {
    return new ApiError({
      status: response.status,
      title: response.statusText || 'Error',
      detail: 'Something went wrong. Please try again.',
      code: 'request_failed',
    });
  }
  return new ApiError(NETWORK_ERROR);
}

/** Returns the data of an openapi-fetch result, or throws an ApiError. */
export function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.error === undefined && result.response.ok && result.data !== undefined) return result.data;
  throw toApiError(result.error, result.response);
}

/** For calls that answer null while there is nothing yet (the server sends an empty body): the data, or null. */
export function unwrapOrNull<T>(result: { data?: T | null; error?: unknown; response: Response }): T | null {
  if (result.error === undefined && result.response.ok) return result.data ?? null;
  throw toApiError(result.error, result.response);
}

/** For calls that answer 204 No Content. */
export function ensureOk(result: { error?: unknown; response: Response }): void {
  if (result.error === undefined && result.response.ok) return;
  throw toApiError(result.error, result.response);
}
